export const PAGE_PATH = '/programa-perda-peso'
export const PAGE_PATH_LEGACY = '/programa-perda-de-peso'

export const PROGRAM_TRAIL_PATH = PAGE_PATH
export const PROGRAM_TRAIL_LEGACY_PATH = '/programa-perda-peso/triagem'
export const PROGRAM_HERO_SLUG = 'programa_perda_peso'

// O produto do programa é identificado pela API (PROGRAM_PRODUCT_ID lá); o pedido chega com `is_program_order`.
// VITE_PROGRAM_PRODUCT_ID só cobre payloads antigos que ainda não trazem o sinal.
export const PROGRAM_PRODUCT_ID = Number(import.meta.env.VITE_PROGRAM_PRODUCT_ID || 0)

// Prescritor da consulta inclusa do programa (id em cn_prescribers), configurado por ambiente.
export const PROGRAM_PRESCRIBER_ID = Number(import.meta.env.VITE_PROGRAM_PRESCRIBER_ID || 0)

export const HEADER_NAV = [
  { href: '#incluido', label: 'O que está incluído' },
  { href: '#valores', label: 'Valores' },
  { href: '#como-funciona', label: 'Como funciona' },
  { href: '#duvidas', label: 'Dúvidas' }
] as const

export const PROGRAM_PRICE = 2697
export const PROGRAM_MONTHS = 3
export const PROGRAM_INSTALLMENTS = 3
export const PROGRAM_PIX_DISCOUNT_PERCENT = 5
export const PROGRAM_INSTALLMENT_VALUE = PROGRAM_PRICE / PROGRAM_INSTALLMENTS
export const PROGRAM_PIX_PRICE = Number(
  (PROGRAM_PRICE * (1 - PROGRAM_PIX_DISCOUNT_PERCENT / 100)).toFixed(2)
)

type TProgramOrderLike = {
  is_program_order?: boolean
  items?: Array<{ product_id?: number; product?: { id?: number } }>
}

export const isProgramProductId = (productId?: number) =>
  Boolean(PROGRAM_PRODUCT_ID) && Number(productId) === PROGRAM_PRODUCT_ID

export const isProgramOrder = (order?: TProgramOrderLike | null) =>
  Boolean(order?.is_program_order) ||
  (Boolean(PROGRAM_PRODUCT_ID) &&
    (order?.items || []).some((item) => Number(item?.product_id || item?.product?.id) === PROGRAM_PRODUCT_ID))

export const PROGRAM_INCLUDED_ITEMS = [
  {
    title: 'Consulta médica por teleatendimento',
    detail: 'Seu médico segue com você durante os 3 meses'
  },
  {
    title: 'Medicamento, quando indicado',
    detail: 'Conforme prescrição do seu médico'
  },
  {
    title: '2 consultas com nutricionista',
    detail: 'Para ajustar sua alimentação'
  },
  {
    title: '1 consulta com psicólogo',
    detail: 'Para cuidar também da parte emocional'
  },
  {
    title: 'Enfermagem contínua',
    detail: 'Apoio para tirar dúvidas e acompanhar sua rotina'
  }
] as const
export const WEIGHT_LOSS_PERCENT = 22
// Perda média ilustrada na tela de IMC (exemplo do design: 80 kg → ~72 kg). Pendente de validação clínica.
export const PROGRAM_ESTIMATED_LOSS_PERCENT = 10

export const MARQUEE_ITEMS = [
  'Avaliação médica inicial',
  '2 consultas com nutricionista',
  'Avaliação psicológica',
  'Acompanhamento do time de enfermagem',
  'Monitoramento da evolução',
  'Cuidado multidisciplinar'
]

export const ROTATOR_WORDS = ['a sua alimentação', 'as suas emoções', 'a sua rotina', 'a sua saúde']

export const TEAM_TABS = [
  {
    id: 'medico',
    label: 'Médico',
    description: 'Avalia sua saúde de forma individualizada e define o seu plano de cuidado.',
    items: [
      'Avaliação médica inicial',
      'Consulta médica de acompanhamento',
      'Medicação durante o período do programa, quando houver indicação clínica e prescrição médica, conforme condições do programa'
    ]
  },
  {
    id: 'nutricionista',
    label: 'Nutricionista',
    description: 'Orientação alimentar adaptada à sua rotina e às suas preferências.',
    items: ['2 consultas com nutricionista']
  },
  {
    id: 'psicologo',
    label: 'Psicólogo',
    description: 'Atenção à relação com a comida, às emoções e ao bem-estar.',
    items: ['Avaliação psicológica']
  },
  {
    id: 'enfermagem',
    label: 'Enfermagem',
    description: 'Um time próximo para orientar e acompanhar a sua evolução.',
    items: ['Acompanhamento do time de enfermagem', 'Monitoramento da evolução ao longo do programa']
  }
] as const

export const formatBrl = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)

export const PRICING_ROWS = [
  { id: 'consulta', label: 'Consulta médica', separatePrice: 450 },
  { id: 'tratamento', label: 'Tratamento para 3 meses*', separatePrice: 1800 },
  { id: 'psicologa', label: 'Consulta com psicóloga', separatePrice: 350 },
  { id: 'nutri', label: '2 consultas com nutricionista', separatePrice: 500 },
  { id: 'enfermagem', label: 'Acompanhamento de enfermagem', separatePrice: 400 }
] as const

export const PRICING_SEPARATE_TOTAL = PRICING_ROWS.reduce((sum, row) => sum + row.separatePrice, 0)

export const JOURNEY_STEPS = [
  {
    n: '1',
    badge: 'Comece aqui',
    title: 'Triagem online',
    text: 'Você responde algumas perguntas e descobre se o programa pode fazer sentido para você.'
  },
  {
    n: '2',
    title: 'Avaliação médica',
    text: 'O médico avalia seu caso de forma individualizada e define o plano de cuidado.'
  },
  {
    n: '3',
    title: 'Cuidado multidisciplinar',
    text: 'Consultas com nutricionista, avaliação psicológica e apoio da enfermagem.'
  },
  {
    n: '4',
    title: 'Acompanhamento',
    text: 'Monitoramento da sua evolução e consulta médica de acompanhamento.'
  }
] as const

export const COMPARE_ALONE = [
  'Sem orientação profissional',
  'Foco só no número da balança',
  'Ninguém acompanhando a sua evolução',
  'Dúvidas sem ter a quem recorrer'
]

export const COMPARE_PROGRAM = [
  'Avaliação médica individualizada',
  'Nutrição, psicologia e enfermagem juntas',
  'Monitoramento da evolução ao longo do programa',
  'Time de enfermagem ao seu lado'
]

export const FAQ_ITEMS = [
  {
    question: 'Para quem é o programa?',
    answer:
      'Para pessoas que desejam perder peso com acompanhamento médico, nutricional, psicológico e de enfermagem. A triagem inicial ajuda a entender se o programa faz sentido para o seu caso.'
  },
  {
    question: 'Vou receber medicação?',
    answer:
      'Somente quando houver indicação clínica e prescrição médica, conforme condições do programa. A decisão sobre medicamento, dose e ajustes é exclusiva do médico responsável, após avaliação individualizada.'
  },
  {
    question: 'O que acontece depois da triagem?',
    answer:
      'Se o programa fizer sentido para o seu caso, o próximo passo é a avaliação médica, que define o plano de cuidado de forma individualizada.'
  },
  {
    question: 'Quanto tempo dura e quanto custa?',
    answer:
      'O programa tem duração de 3 meses e custa R$ 2.697,00. Esse valor pode ser dividido em até 3x, ou pago no PIX com 5% de desconto. O valor reúne o cuidado multidisciplinar e, quando houver indicação clínica e prescrição médica, o tratamento medicamentoso nas condições do programa.'
  }
]

export const SEO_TITLE = 'Programa de perda de peso com acompanhamento médico'
export const SEO_DESCRIPTION =
  'Programa Cannect de perda de peso com médico, nutricionista, psicólogo e enfermagem. Online, R$ 2.697,00 em até 3x ou 5% de desconto no PIX, em um plano de 3 meses.'
export const SEO_KEYWORDS =
  'programa perda de peso, emagrecimento com acompanhamento médico, nutricionista, psicólogo, enfermagem, Cannect, tratamento para emagrecer'
